import { ETelemetryTopOf, RequestTimelinePoint } from 'multirpc-sdk';
import { createSelector, weakMapMemoize } from 'reselect';
import { ChainID } from '@ankr.com/chains-list';
import { IChartData } from '@ankr.com/telemetry';

import { RootState } from 'store';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { mapStatsForRequestsWidget } from 'domains/statistics/store/utils/mapStatsForRequestsWidget';

import { BlockchainsCountsMap, CountriesCountsMap } from '../../types';
import {
  IFetchPublicStatsParams,
  selectPublicStats,
} from '../../actions/fetchPublicStats';
import { aggregateBlockchainsCountsByChainID } from '../../utils/aggregateBlockchainsCountsByChainID';
import { getTopElementsById } from '../../utils/getTopElementsById';
import { sumBlockchainsCountsByChainPaths } from '../../utils/sumBlockchainsCountsByChainPaths';

const timelineFallback: RequestTimelinePoint[] = [];

export const selectPublicBlockchainsCounts = createSelector(
  selectPublicStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.BLOCKCHAIN,
    }),
);

export const selectPublicCountriesCounts = createSelector(
  selectPublicStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.COUNTRY,
    }),
);

export const selectPublicBlockchainsCountsMap = createSelector(
  selectPublicBlockchainsCounts,
  blockchainCounts =>
    blockchainCounts.reduce<BlockchainsCountsMap>((map, { count, name }) => {
      map[mapSharkChainToChainPath(name)] = Number(count);

      return map;
    }, {}),
);

export const selectPublicCountriesCountsMap = createSelector(
  selectPublicCountriesCounts,
  countriesCounts =>
    countriesCounts.reduce<CountriesCountsMap>(
      (countryMap, { count, name: country }) => ({
        ...countryMap,
        [country]: { country, requests: Number(count) },
      }),
      {},
    ),
);

export const selectPublicUsageTimeline = createSelector(
  selectPublicStats,
  ({ request_timelines }) => {
    const usageTimeline = request_timelines.find(
      ({ name }) => name === 'Usage',
    );

    return usageTimeline?.points ?? timelineFallback;
  },
);

export const selectPublicRequestsChartData = createSelector(
  selectPublicUsageTimeline,
  usageTimeline =>
    usageTimeline.map<IChartData>(({ time, value }) => ({
      time: new Date(time),
      value,
    })),
);

export const selectMappedPublicRequestsChartData = createSelector(
  selectPublicRequestsChartData,
  (_state: RootState, statsParams: IFetchPublicStatsParams) => statsParams,
  (data, { timeframe, timeframeShift }) => {
    return mapStatsForRequestsWidget({
      data,
      timeframe,
      timeframeShift,
      shouldHideLastPointIfSmall: true,
    });
  },
);

export const selectPublicTotalRequests = createSelector(
  selectPublicStats,
  ({ total }) => Number(total),
);

export const selectPublicBlockchainCountByChainID = createSelector(
  selectPublicBlockchainsCounts,
  (state: RootState, _params: IFetchPublicStatsParams, chainId: ChainID) => ({
    chainId,
    state,
  }),
  (blockchainsCounts, { chainId, state }) => {
    const chainPaths = selectAllPathsByChainId(state, chainId);

    return sumBlockchainsCountsByChainPaths({
      blockchainsCounts,
      chainPaths,
    });
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPublicAggregatedBlockchainsCountsByChainID = createSelector(
  selectPublicBlockchainsCounts,
  (state: RootState) => state,
  (blockchainsCounts, state) =>
    aggregateBlockchainsCountsByChainID(state, blockchainsCounts),
);
